import { ReactElement, useMemo } from 'react';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useTranslation } from 'react-i18next';
import styles from './InteractionSummaryGI.module.scss';
import AssetInfo, { AssetInfoRow } from '@customer/components/asset-data/asset-info/AssetInfo';
import GoodInvalidRenderer from '@customer/components/tables/cells/good-invalid/GoodInvalidRenderer';
import {
  useDistanceText,
  useSpeedText,
} from '@customer/components/tables/formatters/numberFormatters';
import SpeedRangeRenderer from '@customer/components/tables/cells/speed-range/SpeedRangeRenderer';
import { AssetGIModel, GIType } from '@customer/store/apis/interactionsApi';

interface InteractionSummaryGIProps {
  interaction?: AssetGIModel;
}

export const InteractionSummaryGI = ({ interaction }: InteractionSummaryGIProps): ReactElement => {
  const { t } = useTranslation();
  const { showDurationFromSeconds } = useLocaleFormatter();
  const getSpeedText = useSpeedText();
  const getDistanceText = useDistanceText();

  const {
    dur: duration,
    dist: distance,
    rn: ruleName,
    act: active,
    it,
    lo,
    ro,
    geo,
    svfix,
    evfix,
  } = interaction ?? {};

  const infoRows: AssetInfoRow[] = useMemo(
    () => [
      {
        label: t('GpsFix'),
        value: <GoodInvalidRenderer value={svfix} endFixValid={evfix} />,
      },
      {
        label: t('Duration'),
        value: active
          ? t('Active')
          : typeof duration === 'number'
            ? showDurationFromSeconds(duration)
            : '',
      },
      {
        label: t('Distance'),
        value: typeof distance === 'number' ? getDistanceText(distance) : '',
      },
      {
        label: t('Rule'),
        value: ruleName ?? '',
      },
      {
        label: t('Geofence'),
        // Use the RO name for GeofenceSpeedingRoadworks interactions, otherwise use the geofence name.
        value: (it === GIType.GeofenceSpeedingRoadworks ? ro?.n : geo?.n) ?? '',
      },
      {
        label: t('Asset'),
        value: lo?.n ?? '',
      },
      {
        label: t('EntrySpeed'),
        value: lo?.spd != null ? getSpeedText(lo.spd) : undefined,
      },
      {
        label: t('MinMaxSpeed'),
        value:
          lo?.minSpd != null && lo?.maxSpd != null ? (
            <SpeedRangeRenderer value={[lo.minSpd, lo.maxSpd]} />
          ) : undefined,
      },
    ],
    [
      t,
      active,
      duration,
      distance,
      ruleName,
      it,
      geo?.n,
      lo,
      ro?.n,
      svfix,
      evfix,
      showDurationFromSeconds,
      getSpeedText,
      getDistanceText,
    ],
  );

  return (
    <div className={styles.container}>
      <AssetInfo rows={infoRows} verticalPadding={false} horizontalPadding={true} />
    </div>
  );
};

export default InteractionSummaryGI;
